<template>
  <div class="container">
    <BLoading :active.sync="isLoaderRunning" />

    <div v-if="!isLoadingLongGrassInfo" class="columns">
      <main class="column is-6 long-grass-report">
        <LgHeaderSection />

        <LgJobImagesSection :images="formatImages(longGrass)" />

        <LgPricingSection
          :mowing-cycle="mowingCycle"
          :long-grass-label="formatLabel(longGrass)"
          :original-price="longGrass.scheduleEvent.price"
          :lg-price="longGrass.price"
          :trust-fee="longGrass.scheduleEvent.trust_fee"
          :tax="longGrass.total_tax"
          :total="longGrass.total_price"
          :with-foot-notes="true"
        />

        <LgQuoteFooter
          :is-pending="longGrass.is_pending"
          :is-cancelled="scheduleEvent.status === 'cancelled'"
          :return-dates-available="returnDatesAvailable"
          :displayed-date-on="quoteScheduledOnDate"
        />
      </main>
    </div>
  </div>
</template>

<script>
import { themedComponentMixin } from '@/mixins';
import { mapActions, mapGetters, mapState } from 'vuex';
import LgHeaderSection from '../components/LgHeaderSection.vue';
import LgJobImagesSection from '../components/LgJobImagesSection.vue';
import LgPricingSection from '../components/LgPricingSection.vue';
import LgQuoteFooter from '../components/LgQuoteFooter.vue';
import { lsLongGrassHelpers } from '@/helpers';
import { lsFilters } from 'ls-vue-common';
import _ from 'lodash';
import {
  SERVICE_HISTORY__GET_SCHEDULE,
  SERVICE_HISTORY__FILTER_SCHEDULE_EVENT,
} from '@/store/modules/service-history/types';

export default {
  name: 'LongGrassQuoteReportView',
  components: {
    LgHeaderSection,
    LgJobImagesSection,
    LgPricingSection,
    LgQuoteFooter,
  },
  mixins: [themedComponentMixin],
  computed: {
    ...mapState(['serviceHistory', 'instantQuote']),
    ...mapGetters([SERVICE_HISTORY__FILTER_SCHEDULE_EVENT]),
    propertyId() {
      return this.$route.params.property_id;
    },
    scheduleId() {
      return this.$route.params.schedule_id;
    },
    scheduleEventId() {
      return this.$route.params.scheduleevent_id;
    },
    reportId() {
      return this.$route.params.report_id;
    },
    mowingCycle() {
      const {
        serviceHistory: { schedule },
      } = this;

      return lsLongGrassHelpers.getMowingCycle(schedule);
    },
    scheduleEvent() {
      return this[SERVICE_HISTORY__FILTER_SCHEDULE_EVENT](this.scheduleEventId);
    },
    longGrass() {
      return lsLongGrassHelpers.getLongGrassQuoteFromEvent({
        scheduleEvent: this.scheduleEvent,
      });
    },
    returnDatesAvailable() {
      if (!this.longGrass.return_availabilities) return [];
      return this.longGrass.return_availabilities.map((e) => {
        const [date] = e.split(' ');
        const formattedDate = lsFilters.date({ date });
        return {
          label: formattedDate,
          value: date,
        };
      });
    },
    quoteScheduledOnDate() {
      return _.get(this.longGrass, 'service_schedule_event.created_at') || '';
    },
    isLoadingLongGrassInfo() {
      return _.isEmpty(this.longGrass);
    },
    isLoaderRunning() {
      const { instantQuote, serviceHistory, isLoadingLongGrassInfo: loadingInfo } = this;
      return (
        loadingInfo ||
        instantQuote.loadingStatus.isUpdating ||
        serviceHistory.loadingStatus.isLoading
      );
    },
  },
  async beforeMount() {
    const {
      propertyId,
      scheduleId,
      serviceHistory: { schedule },
    } = this;

    if (_.isEmpty(schedule)) {
      await this.getScheduleHistory({
        property_id: propertyId,
        schedule_id: scheduleId,
      });
    }
  },
  methods: {
    ...mapActions({
      getScheduleHistory: SERVICE_HISTORY__GET_SCHEDULE,
    }),
    formatImages({ images }) {
      const beforeJob = images.filter((img) => img.tag.includes('beforeJob')) || [];
      const afterJob = images.filter((img) => img.tag.includes('afterJob')) || [];
      const formatParams = (img) => ({
        id: img.id,
        src: img.image_url,
        thumbSrc: img.thumbnail_url,
        caption: img.tag.split('.').pop(),
      });

      return {
        before: beforeJob.map(formatParams),
        after: afterJob.map(formatParams),
      };
    },
    formatLabel({ scope, grass_length, is_special_gear_needed }) {
      return lsLongGrassHelpers.getLongGrassLabel({
        scope,
        length: grass_length,
        isSpecialGearNeeded: is_special_gear_needed,
      });
    },
  },
};
</script>
